/** Pieces the org prompt, gate and banner share (spec §9.1). */

import * as React from "react";
import { Alert, Box, Button, Link, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { useMe, useOrgs, useSetActiveOrg } from "@/api/hooks";
import { isLegalVersionChanged, useAcceptOrgLegal, type OrgLegal } from "@/api/legal";
import { changesFrom, legalDocLabel } from "@/features/legal/docs";
import { LegalChangesButton } from "@/features/legal/LegalChanges";
import { clearOrgTermsRequired } from "@/features/legal/orgTermsGate";
import { changeSummary, useOrgName } from "@/features/legal/orgTermsText";

type VersionRow = { version: number; items?: { kind: string }[] };

/** What changed in one document's ``version``: the translated summary
 *  of each change-item kind, once each (``GET /legal/{doc}/versions``). */
function useChangeKinds(doc: "terms" | "dpa", version: number): string[] {
  const versions = useQuery({
    queryKey: ["legal", "versions", doc],
    queryFn: async () => (await api.get<VersionRow[]>(`/legal/${doc}/versions`)).data,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const rows = Array.isArray(versions.data) ? versions.data : [];
  const items = rows.find((r) => r.version === version)?.items ?? [];
  return [...new Set(items.map((i) => i.kind))];
}

/** Links to the versions an admin accepts, with what changed in each. */
export function OrgTermsDocuments({ legal }: { legal: OrgLegal }): React.JSX.Element {
  const { t } = useTranslation();
  const termsKinds = useChangeKinds("terms", legal.latest.terms);
  const dpaKinds = useChangeKinds("dpa", legal.latest.dpa);
  const kinds = [...new Set([...termsKinds, ...dpaKinds])].filter((k) => k !== "initial");
  return (
    <Stack sx={{ gap: 1 }}>
      {kinds.length > 0 && (
        <Box>
          <Typography variant="subtitle2">{t("legal.org.prompt.changes")}</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {kinds.map((k) => (
              <Typography component="li" variant="body2" key={k}>
                {changeSummary(t, k)}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {(["terms", "dpa"] as const).map((doc) => {
        // From the version the org accepted (else the one before) to the latest.
        const from = changesFrom(legal.accepted[doc]?.version, legal.latest[doc]);
        return (
          // One line per document: the change summary above already says
          // what changed, so its "What changed" is a link beside the name.
          <Stack
            key={doc}
            direction="row"
            sx={{ columnGap: 1.5, rowGap: 0.25, alignItems: "center", flexWrap: "wrap" }}
          >
            <Link href={`/legal/${doc}?version=${legal.latest[doc]}`} target="_blank" rel="noopener">
              {t("legal.org.version_link", { doc: legalDocLabel(t, doc), version: legal.latest[doc] })}
            </Link>
            {from && <LegalChangesButton doc={doc} from={from} to={legal.latest[doc]} compact />}
          </Stack>
        );
      })}
    </Stack>
  );
}

/** "Accept for <org>": records exactly the versions shown. A 409
 *  ``legal_version_changed`` refetches them and says so; the next click
 *  accepts what is now shown. */
export function AcceptOrgTermsButton({
  slug,
  legal,
  onAccepted,
}: {
  slug: string;
  legal: OrgLegal;
  onAccepted?: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const accept = useAcceptOrgLegal(slug);
  const name = useOrgName(slug);
  const changed = accept.isError && isLegalVersionChanged(accept.error);
  return (
    <Stack sx={{ gap: 1, alignItems: "flex-start" }}>
      {accept.isError && (
        <Alert severity={changed ? "warning" : "error"} sx={{ alignSelf: "stretch" }}>
          {changed ? t("legal.org.version_changed") : t("legal.org.accept_failed")}
        </Alert>
      )}
      <Button
        variant="contained"
        disabled={accept.isPending}
        onClick={() =>
          accept.mutate(
            {
              terms_version: legal.latest.terms,
              dpa_version: legal.latest.dpa,
              ui_language: i18n.resolvedLanguage ?? i18n.language,
            },
            { onSuccess: () => onAccepted?.() },
          )
        }
      >
        {accept.isPending ? t("legal.accept.accepting") : t("legal.org.accept_for", { org: name })}
      </Button>
    </Stack>
  );
}

/** "Switch organisation": the user's other orgs. Switching is always
 *  allowed (spec §8.4), so a person can leave a lapsed org's context. */
export function OrgSwitchButton({
  slug,
  variant = "outlined",
  color,
}: {
  slug: string;
  variant?: "outlined" | "text" | "contained";
  color?: "inherit" | "primary";
}): React.JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const orgs = useOrgs();
  const setActive = useSetActiveOrg();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const list = Array.isArray(orgs.data) ? orgs.data : [];
  const others = (me.data?.admin_scope.org_member_slugs ?? []).filter((s) => s !== slug);
  const nameOf = (s: string) => list.find((o) => o.slug === s)?.name ?? s;
  return (
    <>
      <Button
        variant={variant}
        color={color}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
      >
        {t("legal.org.gate.switch")}
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {others.length === 0 && (
          <MenuItem disabled>
            <ListItemText>{t("legal.org.gate.no_other_org")}</ListItemText>
          </MenuItem>
        )}
        {others.map((s) => (
          <MenuItem
            key={s}
            disabled={setActive.isPending}
            onClick={() =>
              setActive.mutate(
                { slug: s },
                {
                  onSuccess: () => {
                    clearOrgTermsRequired();
                    setAnchor(null);
                  },
                },
              )
            }
          >
            <ListItemText>{nameOf(s)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
